import { useNavigate } from "react-router-dom";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import FloatingActions from "@/components/layout/FloatingActions";
import KpiGrid from "@/pages/admin/dashboard/components/KpiGrid";
import FinancialHealth from "@/pages/admin/dashboard/components/FinancialHealth";
import LogisticsConfig from "@/pages/admin/dashboard/components/LogisticsConfig";
import QuickPromotion from "@/pages/admin/dashboard/components/QuickPromotion";
import { monthLabel, formatNumber } from "@/utils/format";

const EmptyPlatformBanner = () => {
  const { loadDemo } = useApp();
  const navigate = useNavigate();
  return (
    <div className="glass-panel rounded-lg p-5 md:p-6 border-accent-500/30 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <span className="w-12 h-12 rounded-lg bg-accent-500/20 text-accent-200 flex items-center justify-center shrink-0">
          <i className="ri-database-2-line text-2xl" />
        </span>
        <div className="flex-1">
          <h3 className="editable-title text-lg font-bold">Plataforma iniciada limpia</h3>
          <p className="text-xs md:text-sm text-foreground-600 mt-1">
            Aún no hay datos registrados. Importa tu respaldo en Excel o JSON para poblar el
            catálogo, clientes e inventario, o carga datos de demostración para explorar el sistema.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            onClick={() => navigate("/admin/respaldo")}
            className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-upload-2-line" /> Importar respaldo
          </button>
          <button
            onClick={loadDemo}
            className="px-4 py-2.5 rounded-md border border-accent-500/40 text-accent-200 hover:bg-accent-500/10 text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-magic-line" /> Cargar demostración
          </button>
        </div>
      </div>
    </div>
  );
};

const HeroBanner = () => {
  const { data } = useApp();
  const periodLabel = `${monthLabel(data.period.month)} ${data.period.year}`;
  return (
    <div className="relative overflow-hidden rounded-lg glass-panel border-accent-500/25 mb-6">
      <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-accent-500/10 blur-2xl" />
      <div className="relative px-5 md:px-7 py-6 flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-500/20 text-accent-200 animate-pulse-ring">
              <i className="ri-flashlight-line text-sm" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-accent-300">
              Panel Administrador · Juanka
            </span>
          </div>
          <h1 className="editable-title text-2xl md:text-3xl font-extrabold tracking-tight">
            Gestión Comercial & Red de Distribuidores
          </h1>
          <p className="text-xs md:text-sm text-foreground-600 mt-2 max-w-2xl">
            Tablero ejecutivo de Amway Tienda Digital Domicilios: ventas, utilidades, puntos Amway,
            inventario en consignación y cartera, todo reactivo en tiempo real.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 shrink-0">
          <div className="rounded-lg bg-background-200/50 border border-background-300/40 px-4 py-3">
            <p className="text-[10px] text-foreground-500">Período activo</p>
            <p className="text-sm font-bold text-foreground-950 mt-0.5">{periodLabel}</p>
          </div>
          <div className="rounded-lg bg-background-200/50 border border-background-300/40 px-4 py-3">
            <p className="text-[10px] text-foreground-500">Asesores en red</p>
            <p className="text-sm font-bold text-foreground-950 mt-0.5">
              {formatNumber(data.distributors.length)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { data } = useApp();
  const isEmpty = data.products.length === 0 && data.sales.length === 0;

  return (
    <div className="pb-24">
      <HeroBanner />
      {isEmpty && <EmptyPlatformBanner />}

      <SectionTitle
        text="Indicadores Ejecutivos (KPIs)"
        subtitle="Todos los cálculos se recalculan automáticamente al cambiar el período o registrar movimientos."
      />
      <KpiGrid />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-6">
        <div className="xl:col-span-2">
          <FinancialHealth />
        </div>
        <LogisticsConfig />
      </div>

      <div className="mt-6">
        <QuickPromotion />
      </div>

      <FloatingActions />
    </div>
  );
};

export default AdminDashboard;